import React, { Fragment, useEffect, useState } from "react";
import ActionBar from "../../common/ActionBar";
import labels from "../../../helpers/labels.json";
import _ from "lodash";
import { Form, Formik } from "formik";
import { navigate } from "@reach/router";
import { CreateNeutrals } from "../../../helpers/validationSchema";
import FormField from "../../common/FormField/FormField";
import { PrimaryCTAButton } from "../../common/Buttons";
import useLoader from "../../../hooks/useLoader";
import CaseService from "../../../services/CaseService";
import { getErrorMessage } from "../../../helpers/functions";
import { useSnackbar } from "notistack";
import {
  BackArrow,
  Heading,
  SubHeading,
  SectionLabel,
  StyledForm,
  FieldGrid,
  PasswordToggleRow,
  ChangePasswordLink,
  ActionRow,
  Card,
  CardHeader,
  PageWrap,
} from "../../internal/CreateTranscriber";

const CreateNeutral = ({ id }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState();
  const [changePassword, setChangePassword] = useState(false);

  const breadcrumbs = [
    _.startCase(labels.profiles),
    _.startCase(id ? labels.edit_neutral : labels.create_neutral),
  ];
  const onBreadcrumbClick = (selected) => {
    switch (_.snakeCase(selected)) {
      case "profiles":
        navigate("/dashboard/case-manager/profiles");
        break;
      default:
        break;
    }
  };

  const baseInputs = [
    {
      name: "name",
      type: "text",
      label: "Name of the user",
      required: true,
    },
    {
      name: "email",
      type: "email",
      label: "Email of the user",
      required: true,
    },
    {
      name: "mobile",
      type: "text",
      label: "Phone no of the user",
      required: true,
    },
  ];

  const passwordInput = {
    name: "password",
    type: "password",
    label: "Password",
    required: !id || changePassword,
  };

  const inputs = !id
    ? [...baseInputs, passwordInput]
    : changePassword
      ? [...baseInputs, passwordInput]
      : baseInputs;

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true, message: "Loading..." });
      const { password, changePassword: _changePassword, ...rest } = values;
      const payload = { ...rest };

      if (!id) {
        payload.password = password;
      } else if (changePassword) {
        payload.password = password;
      }

      if (id) {
        const response = await CaseService.updatePassiveNeutral(payload, id);
        if (response) {
          enqueueSnackbar(response.message, {
            variant: "success",
          });
          navigate("/dashboard/case-manager/profiles");
        }
      } else {
        const response = await CaseService.createPassiveNeutral(payload);
        if (response) {
          enqueueSnackbar(response.message, {
            variant: "success",
          });
          navigate("/dashboard/case-manager/profiles");
        }
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  }

  useEffect(() => {
    async function getUsersDetails(id) {
      try {
        setLoader({ state: true, message: "Fetching neutral details..." });
        const response = await CaseService.getIndividualUser(id);
        if (response) {
          setState(response);
        }
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
      }
    }

    if (id) {
      getUsersDetails(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <Fragment>
      <ActionBar {...{ breadcrumbs, onBreadcrumbClick }} />
      <PageWrap>
        <Formik
          initialValues={{
            name: state?.name || "",
            email: state?.email || "",
            mobile: state?.mobile || "",
            password: "",
            changePassword: false,
            type: id ? "EDIT" : "CREATE",
          }}
          validationSchema={CreateNeutrals}
          enableReinitialize
          onSubmit={onFormSubmit}
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            handleChange,
            errors,
            handleSubmit,
            touched,
            handleBlur,
            setFieldValue,
            setFieldError,
          }) => (
            <StyledForm>
              <Form>
                <Card>
                  <CardHeader>
                    <BackArrow
                      onClick={() =>
                        navigate(`/dashboard/case-manager/profiles`)
                      }
                      src={require("../../../assets/images/backArrow.svg")}
                    />
                    <div>
                      <Heading>
                        {_.startCase(
                          id ? labels.edit_neutral : labels.create_neutral,
                        )}
                      </Heading>
                      <SubHeading>
                        {id
                          ? "Update this view only user's details"
                          : "Set up login details for a new view only user"}
                      </SubHeading>
                    </div>
                  </CardHeader>

                  <SectionLabel>Details</SectionLabel>
                  <FieldGrid>
                    {inputs?.map((input, key) => (
                      <FormField
                        white
                        {...{
                          key,
                          input,
                          values,
                          errors,
                          touched,
                          handleBlur,
                          handleChange,
                          setFieldValue,
                        }}
                      />
                    ))}
                  </FieldGrid>

                  {/* show this toggle only in edit mode */}
                  {id && (
                    <PasswordToggleRow>
                      {!changePassword ? (
                        <ChangePasswordLink
                          type="button"
                          onClick={() => {
                            setChangePassword(true);
                            setFieldValue("changePassword", true);
                          }}
                        >
                          Change password
                        </ChangePasswordLink>
                      ) : (
                        <ChangePasswordLink
                          type="button"
                          $muted
                          onClick={() => {
                            setChangePassword(false);
                            setFieldValue("changePassword", false);
                            setFieldValue("password", "");
                            setFieldError("password", undefined);
                          }}
                        >
                          Cancel password change
                        </ChangePasswordLink>
                      )}
                    </PasswordToggleRow>
                  )}

                  <ActionRow>
                    <PrimaryCTAButton onClick={handleSubmit}>
                      {id ? "Update View Only User" : "Create View Only User"}
                    </PrimaryCTAButton>
                  </ActionRow>
                </Card>
              </Form>
            </StyledForm>
          )}
        </Formik>
      </PageWrap>
    </Fragment>
  );
};

export default CreateNeutral;
